<template>
    <div class="congress-schedule-editor">
        <!-- Tabs - vždy viditelné -->
        <Tabs :tabs="tabs" class="forms__field_input--congressSchedule-tabs" />

        <!-- Tab 1: Hlavní program (s dny) -->
        <div v-show="activeTab === 'schedule'" class="forms__field_input--congressSchedule-days">
            <!-- Empty state uvnitř tabu -->
            <div v-if="scheduleDays.length === 0" class="empty-state congress-schedule-empty-state">
                <p class="mb-4">
                    {{ lang('congresses::congresses.schedule.empty') }}
                </p>
                <Button type="button" size="sm" color="brand" variant="inline" @click="addDay">
                    <span class="action-icon">
                        <Icon icon="phosphor/regular/calendar-plus"></Icon>
                    </span>
                    <span class="action-label-text">
                        {{ lang('congresses::congresses.schedule.add_first_day') }}
                    </span>
                </Button>
            </div>

            <!-- Days List (Sortable) -->
            <template v-else>
                <Sortable :list="scheduleDays" item-key="_id" tag="div" @end="onDaysReordered">
                    <template #item="{ element: day, index: dayIndex }">
                        <div class="forms__field_input--congressSchedule-day-item">
                            <!-- Drag handle -->
                            <div class="forms__field_input--congressSchedule-day-handle">
                                <icon
                                    icon="hero/solid/ellipsis-vertical"
                                    class="forms__field_input--congressSchedule-day-icon cursor-grab"
                                ></icon>
                            </div>
                            <!-- Day component -->
                            <div class="forms__field_input--congressSchedule-day-content">
                                <ScheduleDay
                                    :key="day._id"
                                    v-model="scheduleDays[dayIndex]"
                                    :day-index="dayIndex"
                                    @remove="removeDay(dayIndex)"
                                />
                            </div>
                        </div>
                    </template>
                </Sortable>

                <!-- Add Day Button -->
                <div class="congress-schedule-after-days-actions">
                    <Button type="button" size="sm" color="brand" variant="inline" @click="addDay">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/calendar-plus"></Icon>
                        </span>
                        <span class="action-label-text">
                            {{ lang('congresses::congresses.schedule.add_day') }}
                        </span>
                    </Button>
                </div>
            </template>
        </div>

        <!-- Tab 2: Doprovodný program - vždy přístupný -->
        <div v-show="activeTab === 'program'" class="space-y-2">
            <ScheduleEventList
                v-model="programEvents"
                event-type="program"
                :day-index="-1"
                :available-tickets="availableTickets"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import Tabs from '@src/Common/Components/Tabs.vue';
import ScheduleDay from './ScheduleDay.vue';
import ScheduleEventList from './ScheduleEventList.vue';
import type { ScheduleDay as ScheduleDayType } from '../types';
import type { Ticket } from '../types';

const modelValue = defineModel<ScheduleDayType[]>();

// Get available tickets from page props
const availableTickets = computed<Ticket[]>(() => {
    const page = usePage();
    return (page.props.object as any)?.tickets || [];
});

if (!modelValue.value) {
    modelValue.value = [];
}

// Ensure all items have _id for Vue key tracking
const ensureIds = (days: ScheduleDayType[]) => {
    return days.map((day) => ({
        ...day,
        _id: day._id || uuidv4(),
        events: (day.events || []).map((event) => ({
            ...event,
            _id: event._id || uuidv4(),
            ticket_ids: event.ticket_ids || event.tickets || [],
            forbidden_ticket_ids: event.forbidden_ticket_ids || event.forbiddenTickets || [],
            speakers: (event.speakers || []).map((speaker) => ({
                ...speaker,
                _id: speaker._id || uuidv4(),
            })),
        })),
    }));
};

modelValue.value = ensureIds(modelValue.value);

const activeTab = ref<'schedule' | 'program'>('schedule');

const CONTAINER_LABEL = '__program_container__';

// Computed: filtruje kontejnerový den (drží jen program events)
const scheduleDays = computed(() => {
    return (modelValue.value || []).filter((day) => day.date_label !== CONTAINER_LABEL);
});

// Computed: Program events (flat array, bez dnů)
const programEvents = computed({
    get: () => {
        // Extrahovat všechny events type='program' ze všech dnů
        const events = [];
        (modelValue.value || []).forEach((day) => {
            (day.events || []).forEach((event) => {
                if (event.type === 'program') {
                    events.push(event);
                }
            });
        });
        // Seřadit podle sort_order, aby se zachovalo pořadí po uložení
        return events.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    },
    set: (newEvents) => {
        // Aktualizovat sort_order podle nového pořadí
        newEvents.forEach((event, idx) => {
            event.sort_order = idx;
        });

        if (!modelValue.value || modelValue.value.length === 0) {
            modelValue.value = [
                {
                    _id: uuidv4(),
                    date: '',
                    date_label: CONTAINER_LABEL,
                    events: newEvents,
                },
            ];
        } else {
            // Najít nebo vytvořit kontejner
            let container = modelValue.value.find((d) => d.date_label === CONTAINER_LABEL);
            if (!container) {
                container = { _id: uuidv4(), date: '', date_label: CONTAINER_LABEL, events: [] };
                modelValue.value.push(container);
            }
            // Odstranit staré program events ze VŠECH dnů
            modelValue.value.forEach((day) => {
                day.events = (day.events || []).filter((e) => e.type !== 'program');
            });
            // Přidat do kontejneru
            container.events = newEvents;
        }
    },
});

const tabs = computed(() => [
    {
        name: `${lang('congresses::congresses.schedule.type_options.schedule')} (${scheduleDays.value.length})`,
        current: () => activeTab.value === 'schedule',
        click: () => {
            activeTab.value = 'schedule';
        },
    },
    {
        name: `${lang('congresses::congresses.schedule.type_options.side_event')} (${programEvents.value.length})`,
        current: () => activeTab.value === 'program',
        click: () => {
            activeTab.value = 'program';
        },
    },
]);

const addDay = () => {
    if (!modelValue.value) {
        modelValue.value = [];
    }
    const newDay = {
        _id: uuidv4(),
        date: '',
        date_label: '',
        events: [],
        sort_order: scheduleDays.value.length,
    };
    modelValue.value = [...modelValue.value, newDay];
};

const removeDay = (index: number) => {
    if (!confirm(lang('congresses::congresses.schedule.confirm_remove_day'))) {
        return;
    }
    const dayToRemove = scheduleDays.value[index];
    modelValue.value = modelValue.value.filter((day) => day._id !== dayToRemove._id);
};

const onDaysReordered = (event: { oldIndex: number; newIndex: number }) => {
    const days = [...scheduleDays.value];
    const [movedItem] = days.splice(event.oldIndex, 1);
    days.splice(event.newIndex, 0, movedItem);

    days.forEach((day, idx) => {
        day.sort_order = idx;
    });

    // Reconstruct modelValue: container day + reordered schedule days
    const container = (modelValue.value || []).find((d) => d.date_label === CONTAINER_LABEL);
    modelValue.value = container ? [container, ...days] : [...days];
};
</script>
